import { useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Col, Row, Modal, Badge } from "react-bootstrap";
import { AlertCircle, Flag } from "react-feather";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";

import ModalGeneric from "../../../../components/ModalGeneric";
import ClaimCard from "./ClaimCard";
import ClaimModal from "./ClaimModal";
import InspectOptions from "./InspectOptions";
import ProcessingItemData from "./ProcessingItemData";

import printService from "../../../../services/print.service";
import claimService from "../../../../services/claim.service";
import inspectService from "../../../../services/inspect/inspect.service";
import listingService from "../../../../services/listingService";

import { InspectContext } from "../../Inspect";

const InspectItem = () => {
  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [inspectionResult, setInspectionResult] = useState(null);

  const {
    currentItem,
    flags,
    inspectConditionId,
    inspectOptions,
    setIsLoading,
    setMakeClaim,
    setShowClaimModal,
    shouldSubmitInspection,
    showClaimModal,
    userId,
  } = useContext(InspectContext);

  const { processingData } = currentItem;
  const { lpn, receivedAsin: asin, claim } = processingData;

  const claimCardRef = useRef(null);
  const startedAt = useRef(new Date().toISOString());

  // Inspection result badge color map
  const statusColorMap = {
    LISTED: "success",
    PENDING_FNSKU: "secondary",
    NOT_LISTED: "danger",
    LIQUIDATED: "warning",
  };

  // Show a modal to acknowledge flags
  useEffect(() => {
    if (
      currentItem?.processingData?.asinRule?.restrictProcessing ||
      flags?.length > 0
    ) {
      setShowModal(true);
    }
  }, [currentItem, flags]);

  useEffect(() => {
    if (inspectionResult) {
      setCountdown(4);
    }
  }, [inspectionResult]);

  useEffect(() => {
    if (countdown === null) return;

    if (countdown === 0) {
      window.location.href = "/inspecting";
      return;
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);
  }, [countdown]);

  const handleScroll = (ref) => {
    window.scrollTo({
      top: ref.offsetTop,
      left: 0,
      behavior: "smooth",
    });
  };

  const submitInspection = async ({ inspectConditionId }) => {
    setIsLoading(true);

    const response = await inspectService.submitInspection({
      lpn,
      asin,
      upc: currentItem?.processingData?.receivedUpc,
      receivedFnsku: currentItem?.processingData?.receivedFnsku,
      userId,
      startedAt: startedAt.current,
      inspectConditionId,
      quantity: currentItem?.processingData?.receivedQuantity || 1,
      vendorId: currentItem?.processingData?.receivedVendorId,
    });

    if (response.success) {
      const result = response.result;

      const pendingFnsku = result.listingStatus === "PENDING_FNSKU";

      if (!result.listingId) {
        setIsLoading(false);
        toast.error("Error submitting inspection, no listing id created.");
        return;
      } else {
        setInspectionResult(result);
      }

      if (result.listingAccountId !== 8) {
        await printService.printListingLabel({
          fnsku: result.fnsku,
          sku: result.sku,
          amazonConditionLabel: result.amazonConditionLabel,
          inspectConditionCode: result.inspectConditionCode,
          title: result.title,
          listingAccountId: result.listingAccountId,
          listingId: result.listingId,
          pendingFnsku,
          quantity: result.quantity,
        });
      }
    } else {
      setIsLoading(false);
      toast.error(response.message);
    }
  };

  const submitClaim = async ({
    claimReasonId,
    claimSubReasonId,
    claimReasonNote,
    fnsku,
    removalOrderId,
    claimImages,
  }) => {
    setIsLoading(true);

    if (claim) {
      const response = await claimService.updateClaim(claim.reportId, {
        fnsku,
        removalOrderId,
        reasonId: claimReasonId,
        subReasonId: claimSubReasonId,
        reasonNote: claimReasonNote,
        reportImages: claimImages,
      });

      if (response.success) {
        toast.success("Damage Report successfully updated.");
        setShowClaimModal(false);
        setMakeClaim(false);
        processingData.claim = response.updatedReport;
      } else {
        toast.error("Error updating Damage Report.");
      }
      setIsLoading(false);
    } else {
      const response = await claimService.createClaim({
        lpn,
        fnsku,
        removalOrderId,
        reasonId: claimReasonId,
        subReasonId: claimSubReasonId,
        reasonNote: claimReasonNote,
        reporterId: userId,
        vendorId: processingData.receivedVendorId,
        organizationId: processingData.claimsOrganizationId,
        reportImages: claimImages,
      });

      if (response.success) {
        toast.success("Damage Report successfully submitted.");
        setShowClaimModal(false);
        setMakeClaim(false);
        processingData.claim = response.newReport;
      } else {
        toast.error("Error submitting Damage Report.");
      }
      setIsLoading(false);
    }

    if (shouldSubmitInspection) {
      submitInspection({
        inspectConditionId,
      });
    }
  };

  return (
    <>
      {inspectionResult && (
        <Alert variant="info" className="flex-fill alert-blink">
          <div className="alert-message">
            <Alert.Heading>Inspection Result</Alert.Heading>
            <h5>
              Listing ID: {inspectionResult?.listingId ?? "N/A"}{" "}
              <Badge
                bg={
                  statusColorMap[inspectionResult?.listingStatus] || "secondary"
                }
              >
                {inspectionResult?.listingStatus ?? "Unknown"}
              </Badge>
            </h5>
            {countdown !== null && (
              <div className="mb-2">
                <small>
                  <Link to="/inspecting">
                    Reloading in {countdown} second{countdown !== 1 ? "s" : ""}
                    ...
                  </Link>
                </small>
              </div>
            )}
            <hr />
            <Row>
              <Col md={3}>
                <strong>SKU:</strong> {inspectionResult?.sku ?? "N/A"}
              </Col>
              <Col md={3}>
                <strong>ASIN:</strong> {inspectionResult?.asin ?? "N/A"}
              </Col>
              <Col md={3}>
                <strong>FNSKU:</strong> {inspectionResult?.fnsku ?? "N/A"}
              </Col>
              <Col md={3}>
                <strong>Found Price:</strong>{" "}
                {inspectionResult?.foundPrice
                  ? `$ ${inspectionResult.foundPrice.toFixed(2)}`
                  : "N/A"}
              </Col>
              <Col md={3}>
                <strong>Quantity:</strong> {inspectionResult?.quantity ?? "N/A"}
              </Col>
              <Col md={3}>
                <strong>Listing Account:</strong>{" "}
                {inspectionResult?.listingAccount ?? "N/A"}
              </Col>
              <Col md={3}>
                <strong>Disposition:</strong>{" "}
                <Badge bg="primary">
                  {inspectionResult?.disposition ?? "N/A"}
                </Badge>
              </Col>
              <Col md={3}>
                <strong>Amazon Condition:</strong>{" "}
                {inspectionResult?.amazonConditionLabel ?? "N/A"}
              </Col>
              <Col md={3}>
                <strong>Inspect Condition:</strong>{" "}
                {inspectionResult?.inspectConditionLabel ?? "N/A"} (
                {inspectionResult?.inspectConditionCode ?? "?"})
              </Col>
            </Row>
          </div>
        </Alert>
      )}
      <ClaimModal
        claim={claim}
        showClaimModal={showClaimModal}
        setShowClaimModal={setShowClaimModal}
        submitClaim={submitClaim}
        shouldSubmitInspection={shouldSubmitInspection}
        inspectConditionId={inspectConditionId}
        submitInspection={submitInspection}
      />
      {currentItem.processingData.asinRule?.restrictProcessing === true && (
        <ModalGeneric
          showWithoutTrigger={true}
          showCloseButton={false}
          body={`This item should not be processed.`}
          title={`Please Note`}
        />
      )}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        backdrop="static"
      >
        <Modal.Body>
          {currentItem.processingData.asinRule?.restrictProcessing === true && (
            <Alert variant="danger" className="flex-fill alert-blink">
              <div className="alert-icon">
                <AlertCircle
                  className="position-relative top-50 start-50 translate-middle"
                  size={20}
                />
              </div>
              <div className="alert-message font-weight-bold">
                This item should not be processed.
              </div>
            </Alert>
          )}
          {flags.length > 0 && (
            <Alert
              variant="warning"
              className="flex-fill alert-blink mt-3 mb-0"
            >
              <div className="alert-icon">
                <Flag
                  className="position-relative top-50 start-50 translate-middle"
                  size={20}
                />
              </div>
              <div className="alert-message font-weight-bold">
                {flags.map((flag, index) => (
                  <li key={index}>{flag}</li>
                ))}
              </div>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              setShowModal(false);
            }}
          >
            Acknowledge
          </Button>
        </Modal.Footer>
      </Modal>
      <Row>
        <Col xl={8}>
          {currentItem.processingData.asinRule?.restrictProcessing === true && (
            <Alert variant="danger" className="flex-fill alert-blink">
              <div className="alert-icon">
                <AlertCircle
                  className="position-relative top-50 start-50 translate-middle"
                  size={20}
                />
              </div>
              <div className="alert-message font-weight-bold">
                This item should not be processed.
              </div>
            </Alert>
          )}
          {flags.length > 0 && (
            <Alert variant="warning" className="flex-fill alert-blink">
              <div className="alert-icon">
                <Flag
                  className="position-relative top-50 start-50 translate-middle"
                  size={20}
                />
              </div>
              <div className="alert-message font-weight-bold">
                {flags.map((flag, index) => (
                  <li key={index}>{flag}</li>
                ))}
              </div>
            </Alert>
          )}
          {claim && (
            <Alert variant="warning" className="flex-fill alert-blink">
              <div className="alert-icon">
                <AlertCircle
                  className="position-relative top-50 start-50 translate-middle"
                  size={20}
                />
              </div>
              <div className="alert-message font-weight-bold d-flex justify-content-between align-items-center">
                <span>This item has a Damage Report submitted for it.</span>
                <Button
                  variant="warning"
                  onClick={() => {
                    handleScroll(claimCardRef.current);
                  }}
                >
                  Go to Damage Report
                </Button>
              </div>
            </Alert>
          )}
          {currentItem.processingData.inspectedId && (
            <Row>
              <Col>
                <Alert variant="danger" className="flex-fill alert-blink">
                  <div className="alert-icon">
                    <AlertCircle
                      className="position-relative top-50 start-50 translate-middle"
                      size={20}
                    />
                  </div>
                  <div className="alert-message font-weight-bold d-flex justify-content-between align-items-center">
                    <span>
                      This Item was already inspected. Redoing it will overwrite
                      the previous inspection.
                    </span>
                  </div>
                </Alert>
              </Col>
              {!claim &&
                ((processingData.isSignedUpForClaims &&
                  processingData.inspectConditionId > 1) ||
                  // SS: V Brief - if column alwaysTakePicsInspection is true, do the claims process even when condition new is selected,
                  // even if not signed up for claims.
                  processingData.alwaysTakePicsInspection) && (
                  <Col className="text-end" md="auto">
                    <Button
                      variant="primary"
                      onClick={() => {
                        setShowClaimModal(true);
                      }}
                    >
                      Make A Damage Report
                    </Button>
                  </Col>
                )}
            </Row>
          )}
          {currentItem && <ProcessingItemData processingItem={currentItem} />}
          {claim && (
            <div ref={claimCardRef}>
              <ClaimCard claim={claim} setShowClaimModal={setShowClaimModal} />
            </div>
          )}
        </Col>
        {inspectOptions && (
          <Col xl={4}>
            <Card className="sticky-top" style={{ top: 20 }}>
              <Card.Body>
                <InspectOptions
                  submitInspection={submitInspection}
                  optionsGroup={"List"}
                  options={inspectOptions.listOptions}
                />
                <InspectOptions
                  submitInspection={submitInspection}
                  optionsGroup={"Liquidate"}
                  options={inspectOptions.liquidateOptions}
                />
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </>
  );
};

export default InspectItem;
